import { trackMusicModeToggled } from '@src/analytics/EventTracker';
import { MODAL_COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useCaptionsToggle } from './hooks/useCaptionsToggle';
import {
  buildCaptionsPickerOptions,
  HeaderPicker,
  MUSIC_OPTIONS,
  Pickers,
  resolveMusicPicker,
  VOICE_OPTIONS,
} from './ReviewPlayerAudioPickers';
import { captionStyleSource } from './TranslationReviewWorkspace';

import type { AudioMode } from '@src/components/aiVoices/AudioModeSegmentedToggle';
import type { DubMusicMode } from '@src/editor/dubbing/DubEngine';
import type { CustomKaraokeTemplateOption } from '@src/components/textProcessor/TextBrandingService';
import type { CaptionFallbackTemplate } from '@src/editor/services/applyCaptionsLanguage';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

const TOOLTIP_ID = 'player-audio-tooltip';

export interface PlayerAudioPickersCaptions {
  videoCreator: Pick<
    ITranscriptVideoCreator,
    'story' | 'currentVideo' | 'showToast'
  >;
  /** PLAT-10635 — the showcase template to seed a first produce with. */
  captionFallbackTemplate?: CaptionFallbackTemplate;
  /**
   * The showcase's caption templates at the video's aspect ratio, listed
   * under "Workspace Templates": picking one puts its style on the video's
   * captions. Omitted ⇒ On / Off.
   */
  templates?: CustomKaraokeTemplateOption[];
  /** The showcase's name for a template id the list does not carry. */
  resolveTemplateName?: (templateId: string) => string | undefined;
  /** Whether a template id is an Arbor / built-in preset. */
  isPresetTemplate?: (templateId: string) => boolean;
}

export interface PlayerAudioPickersAudio {
  audioMode: AudioMode;
  onAudioModeChange: (mode: AudioMode) => void;
  /**
   * A dub is being placed on the timeline: the Voice picker says so and holds
   * still, as the segmented toggle did, so a second press can't start a
   * second placement.
   */
  busy?: boolean;
  musicMode: DubMusicMode;
  onMusicModeChange?: (mode: DubMusicMode) => void;
  /** The story has a kept, non-silent music bed to lay under the dub. */
  musicAvailable: boolean;
  /** For the music analytics event. */
  storyId?: string;
  /** The dubbed language governed, for the same event. */
  language?: string | null;
}

export interface PlayerAudioPickersProps {
  /**
   * The Captions picker (On / Off), first in the row, on a video with a
   * transcript to caption. Reads the karaoke store of the scope it renders
   * in, so a clip host renders this inside its clip-scoped provider.
   */
  captions?: PlayerAudioPickersCaptions;
  /** Voice and Music, while the language being read has a dub. */
  audio?: PlayerAudioPickersAudio;
  /** Above the surface the pickers sit on (the modal's chrome, the Stage). */
  menuZIndex: number;
}

/**
 * PLAT-11877 — the player's settings row on the surfaces that edit a video:
 * Captions (On / Off), then, while the language being read has a dub, Voice
 * (Original / Dubbed / Both) and Music (None / From Original). The clip
 * preview's header row and Creator Studio's Stage bar. The same pickers
 * Dubbing Review puts beside its player, so "what is on the video" is one
 * control family wherever it plays — and the language menu is left to say
 * which language is being read.
 *
 * Music applies only under the dubbed voice and only when the story has a bed;
 * otherwise the picker is inert with the reason on hover, as in the review.
 * Making the bed (Separate) stays on the Transcript & Speakers tab: it is a
 * story-wide, minutes-long job that neither of these surfaces starts.
 */
export const PlayerAudioPickers: React.FC<PlayerAudioPickersProps> = observer(
  ({ captions, audio, menuZIndex }) => (
    <Row data-testid="player-audio-pickers">
      {captions && <CaptionsPicker {...captions} menuZIndex={menuZIndex} />}
      {audio && <AudioPickers {...audio} menuZIndex={menuZIndex} />}
      {createPortal(
        <Tooltip
          id={TOOLTIP_ID}
          style={{ ...MODAL_COMPACT_TOOLTIP_STYLE, zIndex: menuZIndex + 1 }}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </Row>
  ),
);

/**
 * PLAT-11689 / PLAT-11877 — the video's captions, on or off, as the review's
 * Captions picker reads. Off keeps the produced lines (drawn invisible); On
 * with no track yet produces one, and the row says so.
 */
const CaptionsPicker: React.FC<
  PlayerAudioPickersCaptions & { menuZIndex: number }
> = observer(
  ({
    videoCreator,
    captionFallbackTemplate,
    templates,
    resolveTemplateName,
    isPresetTemplate,
    menuZIndex,
  }) => {
    const captions = useCaptionsToggle(videoCreator, captionFallbackTemplate);
    // The review's rows (PLAT-11877): On / Off, the On row naming the style
    // that is on or that a first "on" applies; then the styles. Here a style
    // row WRITES — the template goes on the video — where the review's only
    // draws.
    const { captionsValue, captionsOptions, triggerLabel, pickFor } =
      buildCaptionsPickerOptions({
        captionsMode: captions.visible ? 'on' : 'off',
        captionsOnHint: captions.hasTrack
          ? 'Over the video'
          : 'Add captions to the video',
        captionTemplateChoice: templates?.length
          ? {
              options: templates,
              selectedId: null,
              onSelect: () => undefined,
              ownStyle: captions.hasTrack
                ? {
                    templateId: captions.templateId,
                    label:
                      (captions.templateId
                        ? resolveTemplateName?.(captions.templateId)
                        : undefined) ?? 'Clip’s Style',
                    source: captionStyleSource(
                      captions.templateId,
                      isPresetTemplate,
                    ),
                  }
                : null,
            }
          : undefined,
      });
    return (
      <HeaderPicker
        label="Captions"
        ariaLabel="Captions"
        testId="player-captions-mode"
        value={captionsValue}
        options={captionsOptions}
        triggerValue={triggerLabel}
        onChange={next => {
          const pick = pickFor(next);
          if (
            pick.kind === 'template' &&
            pick.templateId !== captions.templateId
          ) {
            const template = templates?.find(
              option => option.templateId === pick.templateId,
            );
            if (template) captions.applyTemplate(template);
            return;
          }
          // On, the video's own style, or its own template again: shown.
          captions.setVisible(pick.kind !== 'off');
        }}
        menuZIndex={menuZIndex}
        tooltipId={TOOLTIP_ID}
      />
    );
  },
);

const AudioPickers: React.FC<
  PlayerAudioPickersAudio & { menuZIndex: number }
> = ({
  audioMode,
  onAudioModeChange,
  busy = false,
  musicMode,
  onMusicModeChange,
  musicAvailable,
  menuZIndex,
  storyId,
  language,
}) => {
  // Every host plays a clip, whose engine lays the bed on Both too.
  const { musicInert } = resolveMusicPicker({
    musicAvailable,
    audioMode,
    musicOnBoth: true,
  });
  return (
    <>
      <HeaderPicker
        label="Voice"
        ariaLabel="Voice"
        testId="player-voice-mode"
        value={audioMode}
        options={VOICE_OPTIONS}
        onChange={onAudioModeChange}
        menuZIndex={menuZIndex}
        inertReason={busy ? 'Placing the dubbed voice…' : null}
        triggerValue={busy ? 'Working…' : undefined}
        tooltipId={TOOLTIP_ID}
      />
      <HeaderPicker
        label="Music"
        ariaLabel="Music"
        testId="player-music-mode"
        value={musicMode}
        options={MUSIC_OPTIONS}
        onChange={mode => {
          if (mode !== musicMode && language) {
            trackMusicModeToggled({ story_id: storyId, mode, language });
          }
          onMusicModeChange?.(mode);
        }}
        menuZIndex={menuZIndex}
        inertReason={
          onMusicModeChange ? musicInert : 'Music is not available here'
        }
        tooltipId={TOOLTIP_ID}
      />
    </>
  );
};

// The review's row without its right-pinning: the host row decides the place.
const Row = styled(Pickers)`
  margin-left: 0;
`;
